import { Dispatch } from 'redux';
import _ from 'lodash';

import { config } from '../config';
import { isAccountApplicationUpdate, isExpiredToken, WebsocketFeedParser } from '@tradingblock/api';
import { AccountApplicationWebSocketActions, ApplicationActions, ExpiringTokensWebSocketActions, RootApplicationAction, UiActions } from '../state/actions';
import { AccountApplicationUpdate, AllFeedData, FeedData } from '@tradingblock/types';
import { WebSocketStatuses } from '../types/WebSocket';
import { DataStore } from '../state/store';

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private constructor() {}

  // Singleton instance access
  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }

    return WebSocketService.instance;
  }

  // Initialize WebSocket connection
  public async connect(dispatch: Dispatch<RootApplicationAction>) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(config.tradingApiWebSocketUrl);

        this.ws.onopen = () => {
          dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.OPEN));
          dispatch(ExpiringTokensWebSocketActions.setStatus(WebSocketStatuses.OPEN));
        };

        this.ws.onmessage = (event: MessageEvent) => {
          const eventData = event.data;
          const parser = new WebsocketFeedParser(eventData);
          const parsedEventData = parser.parse() as AllFeedData;

          if (_.isNil(parsedEventData)) {
            console.error('Undefined parsed event data: ', eventData);
            return;
          } else if (!parsedEventData.isValid) {
            console.error('Invalid parsed event data: ', eventData);
            return;
          }

          if (isAccountApplicationUpdate(parsedEventData)) {
            const accountApplicationUpdateData = parsedEventData as FeedData<AccountApplicationUpdate>;
            const reduxState = DataStore.getState();

            if (!reduxState.accountApplicationWebSocket.hasReceivedAccountApplicationUpdateMessage) {
              dispatch(AccountApplicationWebSocketActions.setHasReceivedAccountApplicationUpdateMessage(true));
            }

            if (
              !accountApplicationUpdateData.investigationId &&
              ((reduxState.data.status &&
                reduxState.data.status.state !== accountApplicationUpdateData.accountApplicationStatus) ||
                !reduxState ||
                !reduxState.data ||
                !reduxState.data.status)
            ) {
              dispatch(
                ApplicationActions.requestApplicationStatus({ accountId: accountApplicationUpdateData.accountId })
              );
            }
          } else if (isExpiredToken(parsedEventData)) {
            dispatch(UiActions.setIsAuthSessionExpired(true));
          }
        };

        this.ws.onclose = (event: CloseEvent) => {
          if (event.reason) {
            console.warn(event.reason);
          }

          dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.CLOSED));
          dispatch(ExpiringTokensWebSocketActions.setStatus(WebSocketStatuses.CLOSED));
        };

        this.ws.onerror = error => {
          dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.ERROR));
          dispatch(ExpiringTokensWebSocketActions.setStatus(WebSocketStatuses.ERROR));

          console.error('WebSocket error: ', error);
        };
      }
    } catch (error) {
      dispatch(AccountApplicationWebSocketActions.setStatus(WebSocketStatuses.ERROR));
      dispatch(ExpiringTokensWebSocketActions.setStatus(WebSocketStatuses.ERROR));

      console.error('WebSocket creation error: ', error);
    }
  }

  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView): { success: boolean } {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);

      return { success: true };
    } else if (this.ws) {
      console.warn('Trying to send message to closed/closing WebSocket: ', data);
    }

    return { success: false };
  }

  public shouldTryReconnect() {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }

    return false;
  }

  // Close WebSocket connection
  public closeConnection() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export const webSocketService = WebSocketService.getInstance();
